import React from "react";
import cx from "classnames";
import {
  LetterCloseIcon,
  LinkIcon,
  PencilIcon,
  PrinterIcon,
  LogoWhiteIcon,
  LogoBlackIcon,
  LogoRoundedDarkIcon,
  EyeIcon,
} from "assets";
import { useDynamicWindowWidthFor, useTailwindTheme } from "shared-hooks";
import { Theme } from "shared-types";
import { SCREEN_WIDTH_ENUM } from "component-library";
import { useTranslation } from "i18n";
import ProfileButton from "pages/Profile/components/common/ProfileButton";
import EditButton from "pages/Profile/components/common/EditButton";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import ThemeToggle from "pages/Profile/components/ProfileHeader/components/ThemeToggle";

interface Props {
  isEdit?: boolean;
  toggleEdit?: () => void;
}

const ProfileHeader = ({ isEdit, toggleEdit }: Props) => {
  const { t } = useTranslation();
  const isSmall = useDynamicWindowWidthFor(SCREEN_WIDTH_ENUM.SM);
  const { isPublicView } = usePublicView();
  const [theme] = useTailwindTheme();
  const isDark = theme === Theme.Dark;

  return (
    <header className="mb-6 flex flex-col gap-6">
      <div className="flex-end flex items-center justify-end gap-2">
        {!isPublicView && (
          <>
            <ProfileButton
              isText={!isSmall}
              onClick={toggleEdit}
              className={cx("py-1", {
                "bg-color-brand-one text-white hover:bg-color-brand-one/90":
                  isEdit,
              })}
            >
              {isEdit ? (
                <EyeIcon width={22} height={22} />
              ) : (
                <PencilIcon width={22} height={22} />
              )}
              {!isSmall && (
                <span className="hidden md:block">
                  {isEdit
                    ? t("performer:profile.header.button.profileType.public")
                    : t("performer:profile.header.button.profileType.edit")}
                </span>
              )}
            </ProfileButton>
            <ProfileButton>
              <PrinterIcon />
            </ProfileButton>
            <ProfileButton>
              <LetterCloseIcon />
            </ProfileButton>
            <ProfileButton>
              <LinkIcon />
            </ProfileButton>
          </>
        )}
        <ThemeToggle />
      </div>
      <div>
        <h1 className="flex items-start gap-4 md:items-baseline">
          <span className="text-[48px] uppercase md:flex-none md:text-[60px]">
            Sarah Johnson{" "}
          </span>
          <span className="hidden gap-4 text-s md:flex">
            she/her | SAG-AFTRA {isEdit && <EditButton />}
          </span>
          {isDark ? (
            <LogoWhiteIcon width={210} className="ml-auto hidden lg:block" />
          ) : (
            <LogoBlackIcon width={210} className="ml-auto hidden lg:block" />
          )}
          <LogoRoundedDarkIcon
            width={64}
            height={64}
            className="ml-auto block md:hidden"
          />
        </h1>

        <p className="gap mt-2 hidden max-w-[850px] gap-4 text-sm md:flex">
          Broadway-trained theater performer with extensive experience in
          classical and contemporary productions. Recent highlights include
          Off-Broadway runs and regional theater leads.
          {isEdit && <EditButton />}
        </p>
      </div>
    </header>
  );
};

export default ProfileHeader;
